import { useDescope, useUser } from '@descope/react-sdk'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useIsSupportUser } from '@/lib/app/support-user'
import { getUserInitials } from '@/lib/string/string-utils'
import { cn } from '@/lib/ui/style-helpers'
import { AppRoute } from '@/lib/url/urls'

import { isDevelopment, isPreview, isStaging } from '@/utils/env-utils.ts'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/lemmala-design-system/avatar'
import { Button } from '@/components/lemmala-design-system/button/button'
import { DropdownMenu } from '@/components/lemmala-design-system/dropdown/dropdown-menu'
import { Modal } from '@/components/lemmala-design-system/modal/modal'

import { SUPPORT_ADMIN_RELOAD_DELAY_MS } from './consts'
import { ImpersonateSupportAdmin } from './impersonate-support-admin'
import { USER_PROFILE_TEXT } from './labels'
import { SetPermissionsModal } from './set-permissions'

export interface UserProfileButtonProps {
  /** When true, show user name next to the avatar in the dropdown trigger (e.g. for expanded sidebar). */
  showNameInTrigger?: boolean
}

export const UserProfileButton = ({ showNameInTrigger = false }: UserProfileButtonProps) => {
  const navigate = useNavigate()
  const { logout } = useDescope()
  const { user } = useUser()

  const [supportAdminOpen, setSupportAdminOpen] = useState(false)
  const [setPermissionsOpen, setSetPermissionsOpen] = useState(false)

  const showSetPermissions = isDevelopment || isPreview || isStaging

  // Register Ctrl+Shift+P to open set permissions modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        showSetPermissions &&
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.key === 'P'
      ) {
        event.preventDefault()
        setSetPermissionsOpen(true)
      }
    }

    if (showSetPermissions) {
      document.addEventListener('keydown', handleKeyDown)
    }
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showSetPermissions])

  const isSupportUser = useIsSupportUser()

  if (!user) return null

  const userInitials = isSupportUser ? 'SU' : getUserInitials(user.name ?? '')

  return (
    <div>
      <Modal
        open={supportAdminOpen}
        size='small'
        title={USER_PROFILE_TEXT.SUPPORT_ADMIN}
        footer={null}
        onCancel={() => setSupportAdminOpen(false)}
        onDismiss={() => setSupportAdminOpen(false)}
      >
        <ImpersonateSupportAdmin
          onSuccess={() => {
            setSupportAdminOpen(false)
            setTimeout(() => navigate(0), SUPPORT_ADMIN_RELOAD_DELAY_MS)
          }}
        />
      </Modal>
      <SetPermissionsModal
        isOpen={setPermissionsOpen}
        onClose={() => {
          setSetPermissionsOpen(false)
        }}
      />
      <DropdownMenu
        menuItems={[
          {
            content: USER_PROFILE_TEXT.SUPPORT_ADMIN,
            hide: user.roleNames?.includes('Support') === false,
            onClick: () => {
              setSupportAdminOpen(true)
            },
          },
          {
            content: USER_PROFILE_TEXT.SET_PERMISSIONS,
            hide: !showSetPermissions,
            onClick: () => {
              setSetPermissionsOpen(true)
            },
          },
          {
            content: USER_PROFILE_TEXT.LOG_OUT,
            onClick: async () => {
              await logout()
              navigate(AppRoute.LOGIN)
            },
          },
        ]}
        triggerAppearance={'headless'}
        triggerContent={
          <Button
            variant='ghost'
            className={cn(
              'flex h-8 w-full items-center rounded px-1 text-left',
              showNameInTrigger ? 'justify-start gap-1.5' : 'justify-center px-0',
            )}
          >
            <Avatar size='small'>
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback>{userInitials}</AvatarFallback>
            </Avatar>
            {showNameInTrigger && (
              <span className='min-w-0 truncate text-main-foreground-primary text-body-default'>
                {user.name ?? ''}
              </span>
            )}
          </Button>
        }
      />
    </div>
  )
}
